import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { AuthService } from '../../../core/auth.service';
import { StaffService } from '../../staff/staff.service';
import { CommunicationService } from '../communication.service';

type CallMode = 'voice' | 'video';
type CallScope = 'single' | 'conference';

interface RemoteStreamTile {
  connectionId: string;
  displayName: string;
  stream: MediaStream;
}

@Component({
  selector: 'app-communication-calls',
  templateUrl: './communication-calls.component.html',
  styleUrls: ['./communication-calls.component.scss']
})
export class CommunicationCallsComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('localVideo') localVideo?: ElementRef<HTMLVideoElement>;
  @ViewChildren('remoteVideo') remoteVideos?: QueryList<ElementRef<HTMLVideoElement>>;

  loadingCalls = false;
  loadingUsers = false;
  callMode: CallMode = 'video';
  callScope: CallScope = 'single';
  userSearchTerm = '';
  staffOptions: any[] = [];
  selectedParticipants: any[] = [];
  incomingCall: any | null = null;
  activeCall: any | null = null;
  recentCalls: any[] = [];
  localStream: MediaStream | null = null;
  remoteStreams: RemoteStreamTile[] = [];
  micMuted = false;
  cameraOff = false;

  private peerConnections = new Map<string, RTCPeerConnection>();
  private subs: Subscription[] = [];

  constructor(
    private staffService: StaffService,
    private communication: CommunicationService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    this.loadStaffUsers();
    this.loadCalls();
    this.communication.connectHub().catch(() => {});
    this.bindCallEvents();
  }

  ngAfterViewChecked(): void {
    if (this.localVideo?.nativeElement && this.localStream && this.localVideo.nativeElement.srcObject !== this.localStream) {
      this.localVideo.nativeElement.srcObject = this.localStream;
    }

    this.remoteVideos?.forEach((videoRef, index) => {
      const stream = this.remoteStreams[index]?.stream;
      if (stream && videoRef.nativeElement.srcObject !== stream) {
        videoRef.nativeElement.srcObject = stream;
      }
    });
  }

  async ngOnDestroy(): Promise<void> {
    await this.cleanupMedia();
    this.subs.forEach(sub => sub.unsubscribe());
  }

  get filteredUsers(): any[] {
    const q = this.userSearchTerm.trim().toLowerCase();
    const users = this.staffOptions || [];
    if (!q) return users;

    return users.filter(user => {
      const hay = [
        user.fullName,
        user.email,
        user.department,
        user.position
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }

  get canStartCall(): boolean {
    if (this.callScope === 'single') return this.selectedParticipants.length === 1;
    return this.selectedParticipants.length >= 2;
  }

  async startCall(): Promise<void> {
    if (!this.canStartCall) {
      Swal.fire(
        'Select Participants',
        this.callScope === 'single'
          ? 'Select exactly one participant for a single call.'
          : 'Select at least two participants for a conference call.',
        'info'
      );
      return;
    }

    if (!await this.prepareLocalMedia()) return;

    const staffIds = this.selectedParticipants.map(user => Number(user.id || user.staffId)).filter(Boolean);
    this.communication.startCall({
      callType: this.callMode === 'video' ? 'Video' : 'Voice',
      callScope: this.callScope === 'conference' ? 'Conference' : 'Single',
      title: this.callScope === 'conference' ? `${this.callModeLabel} Conference` : null,
      staffIds
    }).subscribe({
      next: async (call) => {
        this.activeCall = call;
        this.selectedParticipants = [];
        this.userSearchTerm = '';
        await this.communication.joinCall(call.id).catch(() => {});
        this.loadCalls();
      },
      error: async (err) => {
        await this.cleanupMedia(false);
        Swal.fire('Error', err?.error?.message || 'Failed to start call.', 'error');
      }
    });
  }

  async acceptIncomingCall(): Promise<void> {
    if (!this.incomingCall?.id) return;
    this.callMode = String(this.incomingCall.callType || '').toLowerCase() === 'voice' ? 'voice' : 'video';
    if (!await this.prepareLocalMedia()) return;

    const callId = this.incomingCall.id;
    this.communication.acceptCall(callId).subscribe({
      next: async (call) => {
        this.incomingCall = null;
        this.activeCall = call;
        this.upsertCall(call);
        await this.communication.joinCall(call.id).catch(() => {});
      },
      error: async (err) => {
        await this.cleanupMedia(false);
        Swal.fire('Error', err?.error?.message || 'Failed to accept call.', 'error');
      }
    });
  }

  rejectIncomingCall(): void {
    if (!this.incomingCall?.id) return;
    const callId = this.incomingCall.id;
    this.communication.rejectCall(callId).subscribe({
      next: (call) => {
        this.incomingCall = null;
        this.upsertCall(call);
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to reject call.', 'error')
    });
  }

  async endCall(): Promise<void> {
    if (!this.activeCall?.id) return;

    const callId = this.activeCall.id;
    this.communication.endCall(callId).subscribe({
      next: async (call) => {
        this.upsertCall(call);
        await this.cleanupMedia();
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to end call.', 'error')
    });
  }

  setScope(scope: CallScope): void {
    this.callScope = scope;
    if (scope === 'single' && this.selectedParticipants.length > 1) {
      this.selectedParticipants = this.selectedParticipants.slice(0, 1);
    }
  }

  toggleMic(): void {
    this.micMuted = !this.micMuted;
    this.localStream?.getAudioTracks().forEach(track => track.enabled = !this.micMuted);
  }

  toggleCamera(): void {
    this.cameraOff = !this.cameraOff;
    this.localStream?.getVideoTracks().forEach(track => track.enabled = !this.cameraOff);
  }

  formatWhen(value: string): string {
    return value ? new Date(value).toLocaleString() : '-';
  }

  participantNames(call: any): string {
    return (call?.participants || [])
      .map((participant: any) => participant.staffName || participant.email || 'Participant')
      .join(', ');
  }

  private bindCallEvents(): void {
    this.subs.push(
      this.communication.existingCallParticipants$.subscribe(participants => {
        participants.forEach(participant => this.createOffer(participant.connectionId, participant.displayName || 'Participant'));
      }),
      this.communication.callParticipantJoined$.subscribe(participant => {
        this.ensurePeerConnection(participant.connectionId, participant.displayName || 'Participant');
      }),
      this.communication.callParticipantLeft$.subscribe(connectionId => this.removePeer(connectionId)),
      this.communication.incomingCalls$.subscribe(call => this.handleIncomingCall(call)),
      this.communication.callAccepted$.subscribe(call => this.handleCallUpdate(call)),
      this.communication.callRejected$.subscribe(call => this.handleCallUpdate(call)),
      this.communication.offers$.subscribe(payload => this.handleOffer(payload)),
      this.communication.answers$.subscribe(payload => this.handleAnswer(payload)),
      this.communication.iceCandidates$.subscribe(payload => this.handleIceCandidate(payload))
    );
  }

  private async prepareLocalMedia(): Promise<boolean> {
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: this.callMode === 'video'
      });
      this.micMuted = false;
      this.cameraOff = this.callMode !== 'video';
      return true;
    } catch {
      Swal.fire('Media Permission Needed', 'Allow microphone/camera access to start this call.', 'warning');
      return false;
    }
  }

  private ensurePeerConnection(connectionId: string, displayName: string): RTCPeerConnection {
    const existing = this.peerConnections.get(connectionId);
    if (existing) return existing;

    const peer = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    });

    this.localStream?.getTracks().forEach(track => peer.addTrack(track, this.localStream!));

    peer.onicecandidate = (event) => {
      if (event.candidate && this.activeCall?.id) {
        this.communication.sendIceCandidate(this.activeCall.id, connectionId, event.candidate).catch(() => {});
      }
    };

    peer.ontrack = (event) => {
      const stream = event.streams[0];
      if (!stream) return;
      const existingTile = this.remoteStreams.find(tile => tile.connectionId === connectionId);
      if (existingTile) {
        existingTile.stream = stream;
      } else {
        this.remoteStreams = [...this.remoteStreams, { connectionId, displayName, stream }];
      }
    };

    peer.onconnectionstatechange = () => {
      if (['failed', 'closed', 'disconnected'].includes(peer.connectionState)) {
        this.removePeer(connectionId);
      }
    };

    this.peerConnections.set(connectionId, peer);
    return peer;
  }

  private async createOffer(connectionId: string, displayName: string): Promise<void> {
    if (!this.activeCall?.id || !this.localStream) return;
    const peer = this.ensurePeerConnection(connectionId, displayName);
    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    await this.communication.sendOffer(this.activeCall.id, connectionId, offer.sdp || '');
  }

  private async handleOffer(payload: any): Promise<void> {
    if (!this.activeCall?.id) return;
    if (!this.localStream && !await this.prepareLocalMedia()) return;

    const peer = this.ensurePeerConnection(payload.connectionId, payload.displayName || 'Participant');
    await peer.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
    const answer = await peer.createAnswer();
    await peer.setLocalDescription(answer);
    await this.communication.sendAnswer(this.activeCall.id, payload.connectionId, answer.sdp || '');
  }

  private async handleAnswer(payload: any): Promise<void> {
    const peer = this.peerConnections.get(payload.connectionId);
    if (!peer || !payload.sdp) return;
    await peer.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
  }

  private async handleIceCandidate(payload: any): Promise<void> {
    const peer = this.peerConnections.get(payload.connectionId);
    if (!peer || !payload.candidate) return;
    await peer.addIceCandidate({
      candidate: payload.candidate,
      sdpMid: payload.sdpMid,
      sdpMLineIndex: payload.sdpMLineIndex
    });
  }

  private removePeer(connectionId: string): void {
    this.peerConnections.get(connectionId)?.close();
    this.peerConnections.delete(connectionId);
    this.remoteStreams = this.remoteStreams.filter(tile => tile.connectionId !== connectionId);
  }

  private async cleanupMedia(leaveHub = true): Promise<void> {
    const callId = this.activeCall?.id;
    this.peerConnections.forEach(peer => peer.close());
    this.peerConnections.clear();
    this.remoteStreams = [];
    this.localStream?.getTracks().forEach(track => track.stop());
    this.localStream = null;
    this.activeCall = null;
    this.micMuted = false;
    this.cameraOff = false;
    if (leaveHub && callId) {
      await this.communication.leaveCall(callId).catch(() => {});
    }
  }

  private get callModeLabel(): string {
    return this.callMode === 'video' ? 'Video' : 'Voice';
  }

  private loadStaffUsers(): void {
    this.loadingUsers = true;
    this.staffService.getAll({ isActive: true }).subscribe({
      next: (users) => {
        this.staffOptions = users || [];
        this.loadingUsers = false;
      },
      error: () => {
        this.staffOptions = [];
        this.loadingUsers = false;
      }
    });
  }

  private loadCalls(): void {
    this.loadingCalls = true;
    this.communication.getCalls().subscribe({
      next: (calls) => {
        this.recentCalls = calls || [];
        const openCall = this.recentCalls.find(call => call.status === 'Ringing' || call.status === 'Active');
        if (openCall && this.isMyParticipantStatus(openCall, 'Invited')) {
          this.incomingCall = openCall;
        } else if (openCall && this.isMyParticipantStatus(openCall, 'Joined')) {
          this.activeCall = this.activeCall || openCall;
        }
        this.loadingCalls = false;
        this.communication.markMissedCallsSeen().subscribe({ next: () => {}, error: () => {} });
      },
      error: (err) => {
        this.loadingCalls = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load calls.', 'error');
      }
    });
  }

  private upsertCall(call: any): void {
    this.recentCalls = [call, ...this.recentCalls.filter(item => item.id !== call.id)];
  }

  private handleIncomingCall(call: any): void {
    if (this.activeCall?.id === call.id) return;
    this.incomingCall = call;
    this.upsertCall(call);
    Swal.fire({
      toast: true,
      position: 'top-end',
      timer: 6000,
      showConfirmButton: false,
      icon: 'info',
      title: 'Incoming call',
      text: `${call.startedByName || 'Someone'} is calling you`
    });
  }

  private handleCallUpdate(call: any): void {
    this.upsertCall(call);
    if (this.activeCall?.id === call.id) {
      if (call.status === 'Ended') {
        this.cleanupMedia().catch(() => {});
      } else {
        this.activeCall = call;
      }
    }
    if (this.incomingCall?.id === call.id && call.status === 'Ended') {
      this.incomingCall = null;
    }
  }

  private isMyParticipantStatus(call: any, status: string): boolean {
    const user = this.auth.getUser();
    const userId = user?.userId;
    const email = user?.email;
    return (call?.participants || []).some((participant: any) =>
      String(participant.status || '').toLowerCase() === status.toLowerCase() &&
      (
        (!!userId && !!participant.userId && participant.userId === userId) ||
        (!!email && !!participant.email && String(participant.email).toLowerCase() === String(email).toLowerCase())
      )
    );
  }
}
